import { useSocket } from "common/hooks/useSocket";
import React, { useContext, useEffect, useState } from "react";
import { DraftContext } from "../context";
import {
  CardMedia,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DraftPoolEntry, RoomContext } from "App/context";
import BansList from "../BansList";
import PicksList from "./PicksList";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Avatar from "common/components/Avatar";
import { VS } from "assets/icons";
import GenshinButton from "common/components/GenshinButton";
import RequestDialog from "common/components/RequestDialog";
import { ArrowForward, Pause, PlayArrow } from "@mui/icons-material";
import BalancingBansButton from "./AdditionalBansButton";
import { DraftCharacter, TimerState } from "types/Draft/type";
import { formatTimer } from "common/utils";
import ConfirmReplay from "./ConfirmReplay";
import { PlayersNumber, Room } from "types/Room/type";
import PreparationTime from "./PreparationTime";
import { postMatchData } from "service/draft";
import { DraftCharacterDataOut } from "service/draft/type";
import GenshinDialog from "common/components/GenshinDialog";
import { API_URL } from "service/root/rootApi";

type PickReplacementRequest = {
  id: string;
  requester: "Player 1" | "Player 2";
  pickedCharacter: string;
  replacementCharacter: string;
};

const valorantAudio = new Audio("./sounds/spike-valorant.mp3");
const kaamelottAudio = new Audio("./sounds/kaamelott.mp3");

const EndDraft = () => {
  const { t } = useTranslation();
  const socket = useSocket();
  const { picksPlayer1, picksPlayer2, bansPlayer1, bansPlayer2 } =
    useContext(DraftContext);
  const {
    params,
    role,
    admin,
    player1,
    player2,
    player3,
    player4,
    draftModel,
    balancingBansPlayer1,
    balancingBansPlayer2,
    jokerBansPlayer1,
    jokerBansPlayer2,
    draftPool,
    dispatch,
  } = useContext(RoomContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const [picksOrderP1, setPicksOrderP1] =
    useState<DraftCharacter[]>(picksPlayer1);
  const [picksOrderP2, setPicksOrderP2] =
    useState<DraftCharacter[]>(picksPlayer2);
  const [openPreparation, setOpenPreparation] = useState<boolean>(false);
  const [preparationTimer, setPreparationTimer] = useState<number>(
    draftModel.preparationTimer,
  );
  const [preparationTimerState, setPreparationTimerState] =
    useState<TimerState>("Pause");
  const [openReplay, setOpenReplay] = useState<boolean>(false);
  const [switchPlayers, setSwitchPlayers] = useState<boolean>(true);
  const [openPickReplacement, setOpenPickReplacement] = useState(false);
  const [pickedCharacter, setPickedCharacter] = useState("");
  const [replacementCharacter, setReplacementCharacter] = useState("");
  const [pickReplacementRequest, setPickReplacementRequest] =
    useState<PickReplacementRequest | null>(null);
  const isSolo = params.solo === true;
  const is1V1 = params.playersNumber === PlayersNumber._1vs1;
  const is2V2 = params.playersNumber === PlayersNumber._2vs2;

  const subscribeEvents = () => {
    socket.on("picks-order-p1", (order: DraftCharacter[]) => {
      setPicksOrderP1(order);
      if (role === "Player 1") socket.emit("get-draft-pool");
    });
    socket.on("picks-order-p2", (order: DraftCharacter[]) => {
      setPicksOrderP2(order);
      if (role === "Player 2") socket.emit("get-draft-pool");
    });
    socket.on("timer-preparation", (time: number) => setPreparationTimer(time));
    socket.on("timer-state-preparation", (state: TimerState) =>
      setPreparationTimerState(state),
    );
    socket.on(
      "pick-replacement-request",
      (request: PickReplacementRequest | null) => {
        setOpenPickReplacement(false);
        setPickReplacementRequest(request);
      },
    );
    socket.on("replay", (room: Room, switchPlayers: boolean) => {
      if (switchPlayers) socket.emit("send-swap-localrole");
      dispatch({
        ...room,
        player1: room.player1 ?? undefined,
        player2: room.player2 ?? undefined,
        player3: room.player3 ?? undefined,
        player4: room.player4 ?? undefined,
        teamBoxPlayer1: room.teamBoxPlayer1 ?? undefined,
        teamBoxPlayer2: room.teamBoxPlayer2 ?? undefined,
        balancingBansPlayer1: room.balancingBansPlayer1 ?? undefined,
        balancingBansPlayer2: room.balancingBansPlayer2 ?? undefined,
        jokerBansPlayer1: room.jokerBansPlayer1 ?? undefined,
        jokerBansPlayer2: room.jokerBansPlayer2 ?? undefined,
      });
      navigate("/waitingRoom");
    });
  };

  const convertToDataOut = (chars: DraftCharacter[] | undefined) => {
    if (chars === undefined) return;
    if (is1V1) return chars;
    // 2v2 : constellation de chaque joueur, les sièges vides restent undefined.
    else {
      let res: DraftCharacterDataOut[] = [];
      chars.map((char) => {
        const p1 = player1?.box?.find((c) => char.name === c.name);
        const p2 = player2?.box?.find((c) => char.name === c.name);
        const p3 = player3?.box?.find((c) => char.name === c.name);
        const p4 = player4?.box?.find((c) => char.name === c.name);
        res.push({
          ...char,
          p1Constellation: p1?.constellation,
          p2Constellation: p2?.constellation,
          p3Constellation: p3?.constellation,
          p4Constellation: p4?.constellation,
        });
      });
      return res;
    }
  };

  const sendMatchData = async () => {
    await postMatchData({
      player1Id: player1?.id ?? 0,
      player2Id: player2?.id ?? 0,
      player3Id: player3?.id,
      player4Id: player4?.id,
      sheetId: params.sheet.id,
      balancingBansPlayer1: convertToDataOut(balancingBansPlayer1),
      balancingBansPlayer2: convertToDataOut(balancingBansPlayer2),
      jokerBansPlayer1: convertToDataOut(jokerBansPlayer1),
      jokerBansPlayer2: convertToDataOut(jokerBansPlayer2),
      bansPlayer1: convertToDataOut(bansPlayer1) ?? bansPlayer1,
      bansPlayer2: convertToDataOut(bansPlayer2) ?? bansPlayer2,
      picksPlayer1: convertToDataOut(picksPlayer1) ?? picksPlayer1,
      picksPlayer2: convertToDataOut(picksPlayer2) ?? picksPlayer2,
      steps: draftModel.steps,
    });
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (admin && preparationTimerState === "Play" && preparationTimer > 0) {
      intervalId = setInterval(
        () => socket.emit("send-timer-preparation", preparationTimer - 1),
        1000,
      );
    }
    return () => {
      clearInterval(intervalId);
    };
  }, [preparationTimer, preparationTimerState]);

  useEffect(() => {
    if (preparationTimer === 29) valorantAudio.play();
    else if (preparationTimer === 0) kaamelottAudio.play();
  }, [preparationTimer]);
  useEffect(() => {
    if (preparationTimerState === "Pause") {
      valorantAudio.pause();
      kaamelottAudio.pause();
    } else if (preparationTimer <= 29) valorantAudio.play();
  }, [preparationTimerState]);

  useEffect(() => {
    // Un entraînement solo n'est pas un match : rien n'est enregistré.
    if (admin && !isSolo) sendMatchData();
    subscribeEvents();

    return () => {
      valorantAudio.pause();
      kaamelottAudio.pause();
      socket.removeListener("picks-order-p1");
      socket.removeListener("picks-order-p2");
      socket.removeListener("timer-preparation");
      socket.removeListener("timer-state-preparation");
      socket.removeListener("replay");
      socket.removeListener("pick-replacement-request");
    };
  }, []);

  useEffect(() => {
    socket.emit("get-pick-replacement-request");
  }, []);

  const isCaptain = role === "Player 1" || role === "Player 2";
  const ownPicks =
    role === "Player 1"
      ? picksOrderP1
      : role === "Player 2"
        ? picksOrderP2
        : [];
  const pickedNames = new Set(
    [...picksOrderP1, ...picksOrderP2].map(({ name }) => name),
  );
  const availableReplacements = draftPool.filter(
    ({ name, selectable }) => selectable && !pickedNames.has(name),
  );
  const canRespondToPickReplacement =
    isCaptain &&
    pickReplacementRequest !== null &&
    pickReplacementRequest.requester !== role;
  const sendPickReplacement = () => {
    socket.emit("send-pick-replacement-request", {
      pickedCharacter,
      replacementCharacter,
    });
    setOpenPickReplacement(false);
  };
  const respondToPickReplacement = (accepted: boolean) => {
    if (pickReplacementRequest) {
      socket.emit("send-pick-replacement-response", {
        requestId: pickReplacementRequest.id,
        accepted,
      });
    }
  };

  return (
    <Stack
      direction="column"
      gap={1}
      alignItems="start"
      height="100%"
      padding={1}
    >
      <PicksList
        picks={picksOrderP1}
        player="player1"
        onDrop={
          role === "Player 1" || (isSolo && isCaptain)
            ? (order: DraftCharacter[]) =>
                socket.emit("send-picks-order-p1", order)
            : undefined
        }
      />
      <Stack direction="row" height="12rem" width="100%" gap={1}>
        <Stack
          alignItems="start"
          justifyContent="space-between"
          direction="row"
          width="100%"
        >
          <Stack direction="column">
            <BansList
              player="Player 1"
              color={theme.palette.common.player1.banCard}
              banSteps={draftModel.steps.filter(
                (step) =>
                  step.action === "Ban" &&
                  (step.player === "Player 1" || step.player === "Both"),
              )}
              bans={bansPlayer1}
            />
            {(balancingBansPlayer1 || jokerBansPlayer1) && (
              <BalancingBansButton
                balancingBans={balancingBansPlayer1}
                jokerBans={jokerBansPlayer1}
                orientation="right"
                color={theme.palette.common.player1.banCard}
              />
            )}
          </Stack>
          <Stack direction="column" gap={1} alignItems="end">
            <Stack direction="row">
              <Typography
                textOverflow="ellipsis"
                textAlign="right"
                overflow="hidden"
                maxWidth="10rem"
                color={theme.palette.common.player1.text}
              >
                {player1?.pseudo}
                {is2V2 && (
                  <>
                    <br />
                    {player3?.pseudo}
                  </>
                )}
              </Typography>
              <Avatar character={player1?.avatar} />
            </Stack>
          </Stack>
        </Stack>
        <Stack
          height="100%"
          sx={{
            svg: {
              height: "100%",
              width: "100%",
            },
          }}
        >
          <VS />
        </Stack>
        <Stack
          alignItems="end"
          justifyContent="space-between"
          direction="row"
          width="100%"
        >
          <Stack direction="column" gap={1}>
            <Stack direction="row" alignItems="end">
              <Avatar character={player2?.avatar} />
              <Typography
                textOverflow="ellipsis"
                overflow="hidden"
                maxWidth="10rem"
                color={theme.palette.common.player2.text}
              >
                {player2?.pseudo}
                {is2V2 && (
                  <>
                    <br />
                    {player4?.pseudo}
                  </>
                )}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="column">
            <Stack direction="row" justifyContent="end" gap={1}>
              {(balancingBansPlayer2 || jokerBansPlayer2) && (
                <BalancingBansButton
                  balancingBans={balancingBansPlayer2}
                  jokerBans={jokerBansPlayer2}
                  orientation="left"
                  color={theme.palette.common.player2.banCard}
                />
              )}
            </Stack>
            <BansList
              player="Player 2"
              color={theme.palette.common.player2.banCard}
              banSteps={draftModel.steps.filter(
                (step) =>
                  step.action === "Ban" &&
                  (step.player === "Player 2" || step.player === "Both"),
              )}
              bans={bansPlayer2}
            />
          </Stack>
        </Stack>
      </Stack>
      <PicksList
        picks={picksOrderP2}
        player="player2"
        onDrop={
          role === "Player 2" || (isSolo && isCaptain)
            ? (order: DraftCharacter[]) =>
                socket.emit("send-picks-order-p2", order)
            : undefined
        }
      />
      <Stack justifyContent="space-between" direction="row" width="100%">
        <Stack direction="row">
          <PreparationTime
            open={openPreparation}
            setOpen={setOpenPreparation}
            time={formatTimer(preparationTimer)}
          />
          <GenshinButton
            variant="contained"
            onClick={() => setOpenPreparation(true)}
          >
            {t("Préparation : {{time}}", {
              time: formatTimer(preparationTimer),
            })}
          </GenshinButton>
          {preparationTimerState === "Pause" ? (
            <IconButton
              disabled={!admin}
              onClick={() =>
                socket.emit("send-timer-state-preparation", "Play")
              }
            >
              <PlayArrow />
            </IconButton>
          ) : (
            <IconButton
              disabled={!admin}
              onClick={() =>
                socket.emit("send-timer-state-preparation", "Pause")
              }
            >
              <Pause />
            </IconButton>
          )}
        </Stack>
        <Stack direction="row" gap={2}>
          {/* Sans adversaire réel, changer un pick après coup n'a pas de sens en solo. */}
          {isCaptain && !isSolo && (
            <GenshinButton
              variant="contained"
              disabled={
                pickReplacementRequest !== null ||
                ownPicks.length === 0 ||
                availableReplacements.length === 0
              }
              onClick={() => {
                setPickedCharacter("");
                setReplacementCharacter("");
                setOpenPickReplacement(true);
              }}
            >
              {t("Changer un pick")}
            </GenshinButton>
          )}
          <GenshinButton
            variant="contained"
            onClick={() => setOpenReplay(true)}
          >
            {t("Rejouer")}
          </GenshinButton>
          <GenshinButton
            variant="contained"
            onClick={() => {
              socket.emit("send-leave-room");
              navigate("/");
            }}
          >
            {t("Quitter")}
          </GenshinButton>
        </Stack>
      </Stack>
      <ConfirmReplay
        open={openReplay}
        setOpen={setOpenReplay}
        switchPlayers={switchPlayers}
        setSwitchPlayers={setSwitchPlayers}
      />
      <GenshinDialog
        open={openPickReplacement}
        onClose={() => setOpenPickReplacement(false)}
      >
        <DialogTitle>{t("Changer un pick")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("Sélectionne le pick à remplacer et le nouveau personnage.")}
          </Typography>
          <Stack gap={2} mt={2}>
            <FormControl size="small">
              <InputLabel>{t("Pick à remplacer")}</InputLabel>
              <Select
                value={pickedCharacter}
                label={t("Pick à remplacer")}
                onChange={(event) => setPickedCharacter(event.target.value)}
              >
                {ownPicks.map(({ name }) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>{t("Nouveau personnage")}</InputLabel>
              <Select
                value={replacementCharacter}
                label={t("Nouveau personnage")}
                onChange={(event) =>
                  setReplacementCharacter(event.target.value)
                }
              >
                {availableReplacements.map(({ name }: DraftPoolEntry) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <GenshinButton
            variant="contained"
            onClick={() => setOpenPickReplacement(false)}
          >
            {t("Annuler")}
          </GenshinButton>
          <GenshinButton
            variant="contained"
            disabled={
              pickedCharacter === "" ||
              replacementCharacter === "" ||
              pickedCharacter === replacementCharacter
            }
            onClick={sendPickReplacement}
          >
            {t("Demander")}
          </GenshinButton>
        </DialogActions>
      </GenshinDialog>
      <RequestDialog
        key={pickReplacementRequest?.id}
        open={pickReplacementRequest !== null}
        onAccept={
          canRespondToPickReplacement
            ? () => respondToPickReplacement(true)
            : undefined
        }
        onDecline={
          canRespondToPickReplacement
            ? () => respondToPickReplacement(false)
            : undefined
        }
        title={t("Changement de perso - {{player}}", {
          player:
            pickReplacementRequest?.requester === "Player 1"
              ? player1?.pseudo
              : player2?.pseudo,
        })}
        message={
          pickReplacementRequest ? (
            <Stack direction="column" gap={2}>
              <Typography>
                {t(
                  "{{player}} demande de remplacer {{picked}} par {{replacement}}.",
                  {
                    player:
                      pickReplacementRequest.requester === "Player 1"
                        ? player1?.pseudo
                        : player2?.pseudo,
                    picked: pickReplacementRequest.pickedCharacter,
                    replacement: pickReplacementRequest.replacementCharacter,
                  },
                )}
              </Typography>
              <Stack
                direction="row"
                sx={{ img: { width: "7rem" } }}
                alignItems="center"
                gap={2}
                justifyContent="center"
              >
                <CardMedia
                  component="img"
                  src={`${API_URL}assets/genshin/characters/${pickReplacementRequest.pickedCharacter}/thumb.png`}
                  title={pickReplacementRequest.pickedCharacter}
                />
                <Icon
                  sx={{
                    width: "4rem",
                    height: "4rem",
                    svg: { width: "100%", height: "100%" },
                  }}
                >
                  <ArrowForward />
                </Icon>
                <CardMedia
                  component="img"
                  src={`${API_URL}assets/genshin/characters/${pickReplacementRequest.replacementCharacter}/thumb.png`}
                  title={pickReplacementRequest.replacementCharacter}
                />
              </Stack>
            </Stack>
          ) : null
        }
      />
    </Stack>
  );
};

export default EndDraft;
